import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { mobileApi, visitApi, workplaceApi, archiveApi } from '../../services/api';
import DocumentCropperModal from '../../components/DocumentCropperModal';
import { readFile } from '../../utils/cropUtils';
import { getProgressTheme } from '../../utils/progressTheme';
import { formatDate, canMarkVisitAsCompleted } from '../../utils/dateUtils';
import './FieldWorkplaceDetail.css';

interface WorkplaceDetail {
  id: number;
  name: string;
  dangerClass: string;
  employeeCount: number;
  sgkNo?: string;
  address?: string;
  city?: string;
  district?: string;
  contactPerson?: string;
  contactPhone?: string;
  notes?: string;
  cancelledDocuments?: string[];
  monthlyVisits?: Record<number, boolean>;
  progress?: number;
}

// İptal Edilebilir 5 Resmi Belge (Desktop ile tam uyumlu)
const CANCELLABLE_DOC_KEYS = [
  'employeeRepresentative',  // Çalışan Temsilcisi Belgesi
  'machinePeriodicCheck',    // Makine Periyodik Kontrol
  'kkdDelivery',             // KKD Teslim Tutanağı
  'fieldAuditReport',        // Saha Denetim Raporu
  'jobTraining'              // İSG Kurul Karar Tutanağı
];

interface MonthlyVisitItem {
  month: number;
  monthName: string;
  year: number;
  visited: boolean;
  visitId: number | null;
  visitDate: string | null;
  note: string;
}

interface DocumentItem {
  id: number;
  workplaceId: number;
  documentType: string;
  fileName: string;
  originalFileName?: string;
  filePath: string;
  createdAt: string;
  documentDate?: string;
  expiryDate?: string;
}

// 17 Adet Resmi İSG Belge Tanımı
const STANDARD_DOCUMENTS = [
  { key: 'riskAnalysis', label: 'Risk Analizi Raporu', icon: '📊' },
  { key: 'emergencyPlan', label: 'Acil Eylem Planı', icon: '🚨' },
  { key: 'trainingPlan', label: 'Yıllık Eğitim Planı', icon: '🎓' },
  { key: 'workPlan', label: 'Yıllık Çalışma Planı', icon: '📅' },
  { key: 'instructions', label: 'İSG Talimatları', icon: '📜' },
  { key: 'ek2', label: 'İşe Giriş (EK-2) Sağlık Raporları', icon: '🩺' },
  { key: 'trainings', label: 'Eğitimler ve Oryantasyon', icon: '👥' },
  { key: 'employeeRepresentative', label: 'Çalışan Temsilcisi Belgesi', icon: '🤝' },
  { key: 'annualEvaluation', label: 'Yıllık Değerlendirme Raporu', icon: '📋' },
  { key: 'kkdDelivery', label: 'KKD Teslim Tutanağı', icon: '🦺' },
  { key: 'firstAid', label: 'İlkyardımcı Sertifikası', icon: '🩹' },
  { key: 'machinePeriodicCheck', label: 'Makine Periyodik Kontrol', icon: '⚙️' },
  { key: 'electricalInstallationCheck', label: 'Elektrik Tesisatı Kontrolü', icon: '⚡' },
  { key: 'detectionSuggestionBook', label: 'Tespit ve Öneri Defteri Sureti', icon: '📖' },
  { key: 'emergencyDrill', label: 'Acil Durum Tatbikatı Tutanağı', icon: '🔥' },
  { key: 'fieldAuditReport', label: 'Saha Denetim Raporu', icon: '🔍' },
  { key: 'jobTraining', label: 'İSG Kurul Karar Tutanağı', icon: '🏛️' }
];

// Belge türü ve tehlike sınıfına göre yasal geçerlilik süresi (Ay cinsinden)
const getDocValidityMonths = (code: string, danger: string): number | null => {
  const d = (danger || '').toLowerCase();
  const isVery = d.includes('çok') || d.includes('cok') || d.includes('very');
  const isDang = !isVery && (d.includes('tehlikeli') || d.includes('danger'));

  switch (code) {
    case 'riskAnalysis':
    case 'emergencyPlan':
      return isVery ? 24 : isDang ? 48 : 72;
    case 'machinePeriodicCheck':
    case 'electricalInstallationCheck':
    case 'emergencyDrill':
      return 12;
    case 'jobTraining':
      // İSG Kurul Karar Tutanağı: Çok Tehlikeli: 1 ay, Tehlikeli: 2 ay, Az Tehlikeli: 3 ay
      return isVery ? 1 : isDang ? 2 : 3;
    case 'ek2':
      return isVery ? 12 : isDang ? 36 : 60;
    case 'trainings':
      return isVery ? 12 : isDang ? 24 : 36;
    case 'employeeRepresentative':
      return 60;
    case 'firstAid':
      return 36;
    case 'fieldAuditReport':
      return 1;
    case 'trainingPlan':
    case 'workPlan':
    case 'annualEvaluation':
      return 12; // Özel olarak 01 Ocak kuralıyla hesaplanır
    default:
      return null; // Sürekli / Kalıcı evraklar
  }
};

// Evrak için kalan süre rozeti hesaplayıcı (Mevzuat uyumlu)
const renderRemainingBadge = (docCode: string, dangerClass: string = 'low', docs: DocumentItem[]) => {
  if (!docs || docs.length === 0) return null;

  // Sürekli / kalıcı olan belgeler
  if (['instructions', 'kkdDelivery', 'detectionSuggestionBook'].includes(docCode)) {
    return <span className="fwd-remaining-badge perpetual">♾️ Sürekli</span>;
  }

  const latest = docs[docs.length - 1];
  let expiryDate: Date | null = null;

  if (latest.expiryDate) {
    expiryDate = new Date(latest.expiryDate);
  } else {
    const baseDate = latest.documentDate ? new Date(latest.documentDate) : new Date(latest.createdAt);
    if (!isNaN(baseDate.getTime())) {
      if (['trainingPlan', 'workPlan', 'annualEvaluation'].includes(docCode)) {
        expiryDate = new Date(baseDate.getFullYear() + 1, 0, 1, 0, 0, 0, 0);
      } else {
        const months = getDocValidityMonths(docCode, dangerClass);
        if (months !== null) {
          expiryDate = new Date(baseDate);
          expiryDate.setMonth(expiryDate.getMonth() + months);
        }
      }
    }
  }

  if (!expiryDate || isNaN(expiryDate.getTime())) {
    return <span className="fwd-remaining-badge perpetual">♾️ Sürekli</span>;
  }

  const now = new Date();
  const diffMs = expiryDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return (
      <span className="fwd-remaining-badge expired" title={`Bitiş: ${formatDate(expiryDate)}`}>
        ⚠️ Süresi Doldu ({Math.abs(diffDays)} gün)
      </span>
    );
  }

  if (diffDays <= 30) {
    return (
      <span className="fwd-remaining-badge urgent" title={`Bitiş: ${formatDate(expiryDate)}`}>
        ⏰ {diffDays === 0 ? 'Bugün Doluyor' : `${diffDays} Gün Kaldı`}
      </span>
    );
  }

  if (diffDays < 365) {
    const m = Math.floor(diffDays / 30);
    const d = diffDays % 30;
    return (
      <span className="fwd-remaining-badge valid" title={`Bitiş: ${formatDate(expiryDate)}`}>
        ✅ {m} Ay {d > 0 ? `${d} Gün ` : ''}Kaldı
      </span>
    );
  }

  const years = Math.floor(diffDays / 365);
  const remMonths = Math.round((diffDays % 365) / 30);
  return (
    <span className="fwd-remaining-badge valid" title={`Bitiş: ${formatDate(expiryDate)}`}>
      ✅ {years > 0 ? `${years} Yıl ` : ''}{remMonths > 0 ? `${remMonths} Ay ` : ''}Kaldı
    </span>
  );
};

// Türkçe Tehlike Sınıfı Dönüştürücü
const getHazardInfo = (dc: string) => {
  const s = (dc || '').toLowerCase().trim();
  if (s.includes('çok') || s.includes('cok') || s.includes('high') || s.includes('very')) {
    return { label: 'Çok Tehlikeli', className: 'hazard-cok-tehlikeli' };
  }
  if (s.includes('az') || s.includes('low') || s.includes('less')) {
    return { label: 'Az Tehlikeli', className: 'hazard-az-tehlikeli' };
  }
  return { label: 'Tehlikeli', className: 'hazard-tehlikeli' };
};

const FieldWorkplaceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [workplace, setWorkplace] = useState<WorkplaceDetail | null>(null);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [monthlyVisits, setMonthlyVisits] = useState<MonthlyVisitItem[]>([]);
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [cancelledDocs, setCancelledDocs] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  // Titreşimli Geri Bildirim (Haptic Feedback)
  const triggerHaptic = (type: 'light' | 'medium' | 'success' = 'light') => {
    if (typeof window !== 'undefined' && 'navigator' in window && 'vibrate' in navigator) {
      try {
        if (type === 'light') navigator.vibrate(10);
        else if (type === 'medium') navigator.vibrate(25);
        else if (type === 'success') navigator.vibrate([15, 40, 15]);
      } catch (e) {
        // Haptic desteklenmiyorsa sessizce geç
      }
    }
  };

  // Belge Başına Düzenlenme / Yapılış Tarihi State'i (Varsayılan: Bugün)
  const todayStr = new Date().toISOString().split('T')[0];
  const [docDates, setDocDates] = useState<Record<string, string>>({});

  const getDocDate = (key: string) => docDates[key] || todayStr;
  const setDocDate = (key: string, dateVal: string) => {
    setDocDates(prev => ({ ...prev, [key]: dateVal }));
  };

  // Ay Ziyareti Modalı
  const [selectedMonth, setSelectedMonth] = useState<MonthlyVisitItem | null>(null);
  const [monthVisitDate, setMonthVisitDate] = useState('');
  const [monthNote, setMonthNote] = useState('');
  const [savingVisit, setSavingVisit] = useState(false);

  // Belge Kırpma & Çoklu Sayfa Sistemi State'leri
  const [croppingImage, setCroppingImage] = useState<string | null>(null);
  const [pendingDocKey, setPendingDocKey] = useState<string | null>(null);
  const [pendingDocLabel, setPendingDocLabel] = useState<string>('Belge');
  const [accumulatedFiles, setAccumulatedFiles] = useState<File[]>([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadProgressText, setUploadProgressText] = useState<string>('');

  // Sayfalar Açılır Kutu (Akordiyon) State'i
  const [expandedSlots, setExpandedSlots] = useState<Record<string, boolean>>({});
  const toggleSlotExpand = (key: string) => {
    setExpandedSlots(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Belge Görüntüleme Modalı State'leri
  const [viewerDocList, setViewerDocList] = useState<DocumentItem[] | null>(null);
  const [viewerTitle, setViewerTitle] = useState<string>('');
  const [activeViewerIdx, setActiveViewerIdx] = useState<number>(0);
  const [viewerBlobUrl, setViewerBlobUrl] = useState<string | null>(null);
  const [loadingViewer, setLoadingViewer] = useState(false);

  // Gizli Input Referansları
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const fetchDetail = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await mobileApi.getWorkplaceDetail(Number(id));
      if (res && res.success) {
        setWorkplace(res.workplace);
        if (res.currentYear) setCurrentYear(res.currentYear);
        if (res.monthlyVisits) setMonthlyVisits(res.monthlyVisits);
        if (res.documents) setDocuments(res.documents);
        if (res.workplace?.cancelledDocuments) {
          setCancelledDocs(Array.isArray(res.workplace.cancelledDocuments) ? res.workplace.cancelledDocuments : []);
        }
      }
    } catch (err: any) {
      console.error('İşyeri detayları yüklenirken hata:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, [id]);

  // Evrak İptal Et / İptali Geri Al (Paydadan düşürerek kalanın %100 olmasını sağlar)
  const handleToggleCancelDoc = async (docKey: string, docLabel: string) => {
    if (!id) return;
    const isCurrentlyCancelled = cancelledDocs.includes(docKey);
    const confirmMsg = isCurrentlyCancelled
      ? `"${docLabel}" belgesinin iptalini geri almak ve zorunlu evrak listesine tekrar dahil etmek istiyor musunuz?`
      : `"${docLabel}" belgesini bu işyeri için İPTAL ETMEK (muaf tutmak) istiyor musunuz?\n\nBu işlem belgenin ilerleme yüzdesi paydasından çıkarılmasını sağlar ve kalan belgelerle %100 tamamlanabilir.`;

    if (!window.confirm(confirmMsg)) return;

    const updatedCancelled = isCurrentlyCancelled
      ? cancelledDocs.filter(k => k !== docKey)
      : [...cancelledDocs, docKey];

    setCancelledDocs(updatedCancelled);

    try {
      await workplaceApi.update(Number(id), { cancelledDocuments: updatedCancelled });
    } catch (err: any) {
      console.error('İptal durumu kaydedilirken hata:', err);
      alert('İptal durumu güncellenemedi.');
    }
  };

  // Ay Kartına Tıklandığında Modal Aç
  const handleOpenMonthModal = (m: MonthlyVisitItem) => {
    setSelectedMonth(m);
    setMonthNote(m.note || '');
    if (m.visitDate) {
      setMonthVisitDate(m.visitDate);
    } else {
      const mStr = String(m.month).padStart(2, '0');
      const now = new Date();
      const currentMonth = now.getMonth() + 1;
      const todayIso = now.toISOString().split('T')[0];

      if (m.month === currentMonth) {
        setMonthVisitDate(todayIso);
      } else if (m.month < currentMonth) {
        setMonthVisitDate(`${currentYear}-${mStr}-15`);
      } else {
        // Gelecek ay için varsayılan gün
        setMonthVisitDate(`${currentYear}-${mStr}-01`);
      }
    }
  };

  // Ay Ziyaretini Kaydet / Güncelle
  const handleSaveMonthVisit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !selectedMonth) return;

    // KURAL: Ziyaret günü ve saati gelmeden 'YAPILDI' olarak işaretlenemez!
    const timeCheck = canMarkVisitAsCompleted(monthVisitDate, '09:30');
    if (!timeCheck.allowed) {
      alert(timeCheck.reason || 'Ziyaret günü ve saati gelmeden "YAPILDI" olarak işaretlenemez.');
      return;
    }

    try {
      setSavingVisit(true);
      const res = await visitApi.create({
        workplaceId: Number(id),
        visitDate: monthVisitDate,
        startTime: '09:30',
        endTime: '11:30',
        visitType: 'Periyodik Saha Ziyareti',
        status: 'completed',
        notes: monthNote.trim() || `${selectedMonth.monthName} ayı periyodik saha ziyareti yapıldı.`
      });

      if (res && res.success) {
        triggerHaptic('success');
        setSelectedMonth(null);
        fetchDetail();
      }
    } catch (err: any) {
      alert(err.response?.data?.message || err.message || 'Ziyaret kaydedilemedi.');
    } finally {
      setSavingVisit(false);
    }
  };

  // Ay Ziyaretini İptal Et / Sil
  const handleDeleteMonthVisit = async () => {
    if (!selectedMonth || !selectedMonth.visitId) return;
    if (!window.confirm(`${selectedMonth.monthName} ayı ziyaretini iptal edip sistemden kaldırmak istediğinize emin misiniz?`)) {
      return;
    }

    try {
      setSavingVisit(true);
      await visitApi.delete(selectedMonth.visitId);
      triggerHaptic('medium');
      setSelectedMonth(null);
      fetchDetail();
    } catch (err: any) {
      alert(err.response?.data?.message || err.message || 'Ziyaret silinemedi.');
    } finally {
      setSavingVisit(false);
    }
  };

  // Kamera Tetikleyici
  const handleTriggerCamera = (docKey: string, docLabel: string) => {
    setPendingDocKey(docKey);
    setPendingDocLabel(docLabel);
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
      cameraInputRef.current.click();
    }
  };

  // Galeri / Dosya Tetikleyici
  const handleTriggerGallery = (docKey: string, docLabel: string) => {
    setPendingDocKey(docKey);
    setPendingDocLabel(docLabel);
    if (galleryInputRef.current) {
      galleryInputRef.current.value = '';
      galleryInputRef.current.click();
    }
  };

  // Fotoğraf veya Belge Seçildiğinde
  const handleFilesPicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !pendingDocKey || !id) return;

    const fileArray = Array.from(files) as File[];

    // Tek bir görsel seçildiyse -> Doğrudan Kırpma Modalı açılır
    if (fileArray.length === 1 && fileArray[0].type.startsWith('image/')) {
      const file = fileArray[0];
      if (file.size > 30 * 1024 * 1024) {
        alert('Görsel boyutu çok büyük! Maksimum 30MB yükleyebilirsiniz.');
        return;
      }
      try {
        const dataUrl = await readFile(file);
        setCroppingImage(dataUrl);
      } catch (err) {
        console.error('Fotoğraf okuma hatası:', err);
        alert('Fotoğraf yüklenemedi. Lütfen tekrar deneyin.');
      }
    } else {
      // Çoklu görsel veya PDF dosyası seçildiyse -> Kırpmayı atlayıp doğrudan arka arkaya yükle
      await uploadDirectFiles(fileArray, pendingDocKey);
    }
  };

  // Doğrudan Dosya Yükleme Fonksiyonu
  const uploadDirectFiles = async (fileList: File[], docKey: string) => {
    if (!id) return;
    try {
      setUploadingDoc(true);
      let successCount = 0;
      const targetDocDate = getDocDate(docKey);

      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];
        setUploadProgressText(`${i + 1} / ${fileList.length} dosya yükleniyor...`);

        const formData = new FormData();
        formData.append('file', file);
        formData.append('documentType', docKey);
        formData.append('documentDate', targetDocDate);

        try {
          const res = await workplaceApi.uploadDocument(Number(id), formData);
          if (res && res.success) successCount++;
        } catch (uploadErr) {
          console.error(`Dosya yükleme hatası (${file.name}):`, uploadErr);
        }
      }

      if (successCount > 0) {
        alert(`🎉 ${successCount} adet belge başarıyla yüklendi ve işyeri arşivine işlendi.`);
        fetchDetail();
      } else {
        alert('Belgeler yüklenemedi.');
      }
    } catch (err: any) {
      alert(err.response?.data?.message || err.message || 'Yükleme sırasında hata oluştu.');
    } finally {
      setUploadingDoc(false);
      setUploadProgressText('');
      setPendingDocKey(null);
      setAccumulatedFiles([]);
    }
  };

  // Kırpma Tamamlandığında (Kırp ve Eklemeye Devam Et VEYA Kırp ve Kaydet)
  const handleCropComplete = async (croppedFile: File, continueAdding: boolean = false) => {
    setCroppingImage(null);

    const allFiles = [...accumulatedFiles, croppedFile];

    if (continueAdding) {
      // "Kırp ve Eklemeye Devam Et": Biriktirilen dosyalara ekle
      // (Not: cameraInputRef onTriggerNextUpload içinde Safari için senkron olarak tetiklenmiştir)
      setAccumulatedFiles(allFiles);
    } else {
      // "Kırp ve Kaydet": Tüm biriktirilen sayfaları sırayla backend'e yükle
      if (!id || !pendingDocKey) return;

      try {
        setUploadingDoc(true);
        setAccumulatedFiles([]);
        let successCount = 0;
        const targetDocDate = getDocDate(pendingDocKey);

        for (let i = 0; i < allFiles.length; i++) {
          const file = allFiles[i];
          setUploadProgressText(`${i + 1} / ${allFiles.length} sayfa yükleniyor...`);

          const formData = new FormData();
          formData.append('file', file);
          formData.append('documentType', pendingDocKey);
          formData.append('documentDate', targetDocDate);

          try {
            const res = await workplaceApi.uploadDocument(Number(id), formData);
            if (res && res.success) successCount++;
          } catch (e) {
            console.error('Sayfa yükleme hatası:', e);
          }
        }

        if (successCount > 0) {
          alert(`🎉 Toplam ${successCount} sayfa başarıyla kırpılıp işyeri arşivine kaydedildi!`);
          fetchDetail();
        } else {
          alert('Sayfalar yüklenemedi.');
        }
      } catch (err: any) {
        alert(err.response?.data?.message || err.message || 'Belge yüklenirken bir hata oluştu.');
      } finally {
        setUploadingDoc(false);
        setUploadProgressText('');
        setPendingDocKey(null);
      }
    }
  };

  // Belge Silme (KOORDİNATÖR ONAYI YOK, 24 SAAT SINIRI YOK, DAİMA MEVCUT)
  const handleDeleteDocument = async (docId: number, docLabel: string) => {
    if (!window.confirm(`"${docLabel}" belgesini silmek istediğinize emin misiniz? Arşivden ve sistemden tamamen kaldırılacaktır.`)) {
      return;
    }

    try {
      setLoading(true);
      const res = await mobileApi.deleteDocument(docId);
      if (res && res.success) {
        alert('🗑️ Belge başarıyla silindi ve arşiv güncellendi.');
        fetchDetail();
      }
    } catch (err: any) {
      alert(err.response?.data?.message || err.message || 'Belge silinemedi.');
    } finally {
      setLoading(false);
    }
  };

  // Belge Görüntüleyiciyi Aç (Blob ile %100 Garantili Görüntüleme)
  const openDocViewer = async (docs: DocumentItem[], title: string, startIndex: number = 0) => {
    if (!docs || docs.length === 0) return;
    setViewerDocList(docs);
    setViewerTitle(title);
    setActiveViewerIdx(startIndex);
    await loadViewerBlob(docs[startIndex].id);
  };

  const loadViewerBlob = async (docId: number) => {
    try {
      setLoadingViewer(true);
      if (viewerBlobUrl) {
        URL.revokeObjectURL(viewerBlobUrl);
        setViewerBlobUrl(null);
      }
      const objectUrl = await archiveApi.previewDocument(docId);
      setViewerBlobUrl(objectUrl);
    } catch (err) {
      console.error('Önizleme yükleme hatası:', err);
      alert('Belge önizlemesi yüklenirken bir sorun oluştu.');
    } finally {
      setLoadingViewer(false);
    }
  };

  const handleViewerPageSelect = async (idx: number) => {
    if (!viewerDocList || !viewerDocList[idx]) return;
    setActiveViewerIdx(idx);
    await loadViewerBlob(viewerDocList[idx].id);
  };

  const closeDocViewer = () => {
    if (viewerBlobUrl) {
      URL.revokeObjectURL(viewerBlobUrl);
    }
    setViewerDocList(null);
    setViewerBlobUrl(null);
    setActiveViewerIdx(0);
  };

  if (loading && !workplace) {
    return (
      <div style={{ minHeight: '80vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div className="field-spinner-large" style={{ borderTopColor: '#2563eb' }}></div>
        <p style={{ marginTop: '16px', fontWeight: 600, color: '#334155' }}>İşyeri Bilgileri Yükleniyor...</p>
      </div>
    );
  }

  if (!workplace) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <p>İşyeri bulunamadı.</p>
        <button onClick={() => navigate('/field/dashboard')} className="fwd-back-btn">
          ← Saha Paneline Dön
        </button>
      </div>
    );
  }

  const hazard = getHazardInfo(workplace.dangerClass);
  const currentMonthNum = new Date().getMonth() + 1;
  const completedCount = monthlyVisits.filter(v => v.visited).length;

  // İlerleme Hesabı: İptal edilen evraklar paydadan düşülür ve kalan belgeler üzerinden %100 hesaplanır
  const activeDocs = STANDARD_DOCUMENTS.filter(d => !cancelledDocs.includes(d.key));
  const uploadedDocCount = activeDocs.filter(doc => documents.some(d => d.documentType === doc.key)).length;
  const docScore = activeDocs.length > 0
    ? Math.round((uploadedDocCount / activeDocs.length) * 50)
    : 50;

  // Saha Ziyareti Skoru: Yalnızca içinde bulunulan cari ayda yapılmış/işaretlenmiş ziyaret varsa +50 puan
  const currentMonthItem = monthlyVisits.find(v => v.month === currentMonthNum);
  const isCurrentMonthVisited = !!(
    (currentMonthItem && currentMonthItem.visited) ||
    (workplace.monthlyVisits && (workplace.monthlyVisits[currentMonthNum] || (workplace.monthlyVisits as any)[String(currentMonthNum)]))
  );
  const hasAnyVisit = isCurrentMonthVisited;
  const visitScore = isCurrentMonthVisited ? 50 : 0;
  const totalProgress = Math.min(100, visitScore + docScore);
  const progressTheme = getProgressTheme(totalProgress);

  return (
    <div className="fwd-container">
      {/* Gizli Kamera Input (Direkt Arka Kamerayı Açar - Tüm Mobil Kameralar ve Formatlar) */}
      <input
        type="file"
        ref={cameraInputRef}
        style={{ display: 'none' }}
        accept="image/*,image/heic,image/heif"
        capture="environment"
        onChange={handleFilesPicked}
      />

      {/* Gizli Galeri / Dosya Input (Tüm Görseller, PDF, Word, Excel) */}
      <input
        type="file"
        ref={galleryInputRef}
        style={{ display: 'none' }}
        accept="image/*,image/heic,image/heif,.pdf,application/pdf,.doc,.docx,.xls,.xlsx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        multiple
        onChange={handleFilesPicked}
      />

      {/* 1. Header Card */}
      <header className="fwd-header-card">
        <button
          type="button"
          className="fwd-back-btn"
          onClick={() => navigate('/field/dashboard')}
        >
          ← Saha Paneline Dön
        </button>

        <div className="fwd-title-row">
          <h1 className="fwd-title">{workplace.name}</h1>
          <span className={`field-hazard-badge ${hazard.className}`}>
            {hazard.label}
          </span>
        </div>
      </header>

      {/* 2. Firma Özet Bilgileri */}
      <section className="fwd-summary-card">
        <div className="fwd-info-grid">
          <div className="fwd-info-item">
            <span>👥</span>
            <span><strong>{workplace.employeeCount}</strong> Çalışan</span>
          </div>

          {workplace.contactPhone && (
            <div className="fwd-info-item">
              <span>📞</span>
              <a href={`tel:${workplace.contactPhone}`} className="fwd-phone-link">
                {workplace.contactPhone}
              </a>
            </div>
          )}

          {workplace.contactPerson && (
            <div className="fwd-info-item">
              <span>👤</span>
              <span>{workplace.contactPerson}</span>
            </div>
          )}

          {workplace.sgkNo && (
            <div className="fwd-info-item">
              <span>🏷️</span>
              <span style={{ fontSize: '11px', color: '#64748b' }}>SGK: {workplace.sgkNo}</span>
            </div>
          )}

          <div className="fwd-info-item full-width">
            <span>📍</span>
            <span>{workplace.address || `${workplace.city || ''} / ${workplace.district || ''}`}</span>
            {workplace.address && (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(workplace.address)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="fwd-map-link"
              >
                (Harita)
              </a>
            )}
          </div>
        </div>
      </section>

      {/* 2.5 Genel İlerleme Yüzdesi ve Durum Kartı */}
      <section className="fwd-progress-card">
        <div className="fwd-progress-top">
          <div className="fwd-progress-title-group">
            <span className="fwd-progress-icon">📊</span>
            <span className="fwd-progress-title">Genel İlerleme Durumu</span>
          </div>
          <span
            className="fwd-progress-badge"
            style={{
              backgroundColor: progressTheme.badgeBg,
              color: progressTheme.textColor,
              border: `1px solid ${progressTheme.borderTint}`
            }}
          >
            %{totalProgress}
          </span>
        </div>

        <div className="fwd-progress-track">
          <div
            className="fwd-progress-fill"
            style={{
              width: `${totalProgress}%`,
              background: progressTheme.barGradient
            }}
          />
        </div>

        <div className="fwd-progress-breakdown">
          <span className="fwd-progress-pill">
            📅 Bu Ayki Ziyaret: {hasAnyVisit ? '✅ Tamamlandı (+%50)' : '⏳ Bekliyor (%0)'}
          </span>
          <span className="fwd-progress-pill">
            📁 Belgeler: {uploadedDocCount} / {activeDocs.length} Aktif (+%{docScore})
          </span>
          {cancelledDocs.length > 0 && (
            <span className="fwd-progress-pill cancelled">
              🚫 {cancelledDocs.length} Belge Muaf/İptal
            </span>
          )}
        </div>
      </section>

      {/* Yükleme Süreci Bildirimi */}
      {uploadingDoc && (
        <div style={{
          background: '#eff6ff',
          border: '1.5px solid #60a5fa',
          borderRadius: '10px',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div className="field-spinner-small" style={{ borderTopColor: '#2563eb' }}></div>
          <div>
            <strong style={{ color: '#1e40af', fontSize: '13px' }}>Belgeler Arşivleniyor...</strong>
            <p style={{ margin: 0, fontSize: '11.5px', color: '#3b82f6' }}>{uploadProgressText || 'Lütfen bekleyin...'}</p>
          </div>
        </div>
      )}

      {/* 3. ÜSTTE AYLIK ZİYARETLER (12 AY IZGARASI - TAMAMEN EŞİT KUTULAR) */}
      <section className="fwd-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <h2 className="fwd-section-title" style={{ margin: 0 }}>
            <span>📅 Aylık Ziyaretler ({currentYear})</span>
            <span className="fwd-section-count">{completedCount} / 12 Ay</span>
          </h2>
          <button
            type="button"
            onClick={() => navigate('/field/planning')}
            style={{
              background: '#2563eb',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              padding: '4px 10px',
              fontSize: '11px',
              fontWeight: 750,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              boxShadow: '0 2px 4px rgba(37, 99, 235, 0.2)'
            }}
          >
            ➕ Planlama Yap
          </button>
        </div>

        <div className="fwd-months-grid">
          {monthlyVisits.map((m) => {
            const isCurrent = m.month === currentMonthNum;
            return (
              <div
                key={m.month}
                className={`fwd-month-card ${m.visited ? 'visited' : ''} ${isCurrent ? 'current' : ''}`}
              >
                <div className="fwd-month-header">
                  <span className="fwd-month-name">{m.monthName}</span>
                  {isCurrent && <span className="fwd-month-badge-current">Bu Ay</span>}
                </div>

                <div className="fwd-month-actions">
                  <button
                    type="button"
                    className={`fwd-month-toggle-btn ${m.visited ? 'visited' : 'unvisited'}`}
                    onClick={() => handleOpenMonthModal(m)}
                  >
                    {m.visited ? '✓ Ziyaret Edildi' : 'Ziyaret İşaretle'}
                  </button>

                  <button
                    type="button"
                    className={`fwd-month-note-btn ${m.note ? 'has-note' : ''}`}
                    onClick={() => handleOpenMonthModal(m)}
                  >
                    {m.note ? '📝 Not Var' : '📝 Not Ekle'}
                  </button>
                </div>

                <div className="fwd-month-note-container">
                  {m.note ? (
                    <div className="fwd-month-note-preview" title={m.note}>
                      {m.note}
                    </div>
                  ) : (
                    <div className="fwd-month-note-placeholder">-</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 4. ALTINDA RESMİ İSG BELGESİ SLOTLARI (RENKLİ & İPTAL MEKANİZMALI) */}
      <section className="fwd-section">
        <h2 className="fwd-section-title">
          <span>📋 İSG Belgeleri & Defter Suretleri</span>
          <span className="fwd-section-count">{uploadedDocCount} / {activeDocs.length} Aktif Belge</span>
        </h2>

        <div className="fwd-docs-list">
          {STANDARD_DOCUMENTS.map((docDef) => {
            const isCancelled = cancelledDocs.includes(docDef.key);
            const slotDocs = documents.filter(d => d.documentType === docDef.key);
            const hasFiles = slotDocs.length > 0;
            const isCancellable = CANCELLABLE_DOC_KEYS.includes(docDef.key);

            return (
              <div
                key={docDef.key}
                className={`fwd-doc-slot ${isCancelled ? 'is-cancelled' : hasFiles ? 'has-file' : 'is-missing'}`}
              >
                <div className="fwd-doc-top">
                  <div className="fwd-doc-title-wrapper">
                    <span className="fwd-doc-icon">{docDef.icon}</span>
                    <span className="fwd-doc-title">{docDef.label}</span>
                  </div>

                  <div className="fwd-doc-top-right">
                    {isCancelled ? (
                      <span className="fwd-doc-status cancelled">🚫 İptal Edildi (Muaf)</span>
                    ) : hasFiles ? (
                      <>
                        <span className="fwd-doc-status uploaded">🟢 Yüklendi ({slotDocs.length} Dosya)</span>
                        {renderRemainingBadge(docDef.key, workplace?.dangerClass, slotDocs)}
                      </>
                    ) : (
                      <span className="fwd-doc-status missing">🔴 Eksik (YOK)</span>
                    )}

                    {isCancellable && (
                      <button
                        type="button"
                        className={`fwd-doc-btn-cancel-toggle ${isCancelled ? 'uncancel' : 'cancel'}`}
                        onClick={() => handleToggleCancelDoc(docDef.key, docDef.label)}
                        title={isCancelled ? 'İptali Kaldır ve Zorunlu Yap' : 'Bu Belgeyi İptal Et / Muaf Tut'}
                      >
                        {isCancelled ? '↩️ İptali Geri Al' : '🚫 İptal Et'}
                      </button>
                    )}
                  </div>
                </div>

                {/* İptal Edildi Açıklama Bildirimi */}
                {isCancelled && (
                  <div className="fwd-doc-cancelled-alert">
                    ℹ️ Bu belge bu işyeri için muaf/iptal edilmiştir. İlerleme yüzdesi kalan {activeDocs.length} zorunlu belge üzerinden %100 tamamlanacak şekilde hesaplanır.
                  </div>
                )}

                {/* Yüklenmiş Belgeler / Sayfalar Tek Satır Özeti (1, 2, 3.. Sayfaları Tıklanabilir Rozetler) */}
                {hasFiles && (
                  <div className="fwd-doc-grouped-container">
                    <div className="fwd-doc-pages-header">
                      <span className="fwd-doc-pages-title">
                        📑 Toplam <strong>{slotDocs.length}</strong> Sayfa / Dosya
                      </span>
                      <div className="fwd-doc-pages-quick-actions">
                        <button
                          type="button"
                          className="fwd-btn-view-all"
                          onClick={() => openDocViewer(slotDocs, docDef.label, 0)}
                          title="Tüm Sayfaları Önizlemede Aç"
                        >
                          👁️ Belgeyi Aç (1-{slotDocs.length})
                        </button>
                        <button
                          type="button"
                          className="fwd-btn-toggle-expand"
                          onClick={() => toggleSlotExpand(docDef.key)}
                        >
                          {expandedSlots[docDef.key] ? '▲ Sayfa Listesini Kapat' : '▼ Sayfaları Göster'}
                        </button>
                      </div>
                    </div>

                    {/* Hızlı Sayfa Seçim Hapları (1, 2, 3, 4, 5...) */}
                    <div className="fwd-doc-page-pills">
                      {slotDocs.map((doc, idx) => (
                        <button
                          key={doc.id}
                          type="button"
                          className="fwd-page-pill"
                          onClick={() => openDocViewer(slotDocs, docDef.label, idx)}
                          title={`${idx + 1}. Sayfa: ${doc.originalFileName || doc.fileName}`}
                        >
                          <span className="pill-num">Sayfa {idx + 1}</span>
                        </button>
                      ))}
                    </div>

                    {/* İsteğe Bağlı Açılır Detaylı Sayfa Listesi (Tek tek silme veya inceleme için) */}
                    {expandedSlots[docDef.key] && (
                      <div className="fwd-doc-expanded-pages">
                        {slotDocs.map((doc, idx) => (
                          <div key={doc.id} className="fwd-doc-file-item">
                            <div className="fwd-doc-file-info">
                              <span className="fwd-doc-file-name" title={doc.originalFileName || doc.fileName}>
                                📄 {idx + 1}. Sayfa: {doc.originalFileName || doc.fileName}
                              </span>
                              <span className="fwd-doc-file-date">
                                {doc.documentDate ? `📅 ${formatDate(doc.documentDate)}` : (doc.createdAt ? formatDate(doc.createdAt) : '')}
                              </span>
                            </div>

                            <div className="fwd-doc-file-actions">
                              <button
                                type="button"
                                className="fwd-doc-btn-preview"
                                onClick={() => openDocViewer(slotDocs, docDef.label, idx)}
                                title="Bu Sayfayı Görüntüle"
                              >
                                👁️ Görüntüle
                              </button>

                              <button
                                type="button"
                                className="fwd-doc-btn-delete"
                                onClick={() => handleDeleteDocument(doc.id, `${docDef.label} - ${idx + 1}. Sayfa`)}
                                title="Bu Sayfayı Sil"
                              >
                                🗑️ Sil
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Belge Düzenlenme / Yapılış Tarihi Giriş Kutusu */}
                {!isCancelled && (
                  <div className="fwd-doc-date-row">
                    <label className="fwd-doc-date-label" htmlFor={`fwd-doc-date-${docDef.key}`}>
                      <span>📅 Belge Düzenleme / Yapılış Tarihi:</span>
                    </label>
                    <input
                      id={`fwd-doc-date-${docDef.key}`}
                      type="date"
                      className="fwd-doc-date-input"
                      value={getDocDate(docDef.key)}
                      onChange={(e) => setDocDate(docDef.key, e.target.value)}
                      title="Belgenin düzenlendiği veya yapıldığı tarihi seçin (Kalan süre bu tarihe göre hesaplanır)"
                    />
                  </div>
                )}

                {/* Belge Ekleme Aksiyonları: Kamera ve Dosya/Galeri Butonları */}
                <div className="fwd-doc-actions">
                  <button
                    type="button"
                    className="fwd-doc-btn-camera"
                    onClick={() => handleTriggerCamera(docDef.key, docDef.label)}
                    disabled={uploadingDoc || isCancelled}
                  >
                    <span>📸</span>
                    <span>{hasFiles ? 'Yeni Sayfa Çek (Kırp)' : 'Fotoğraf Çek (Kırp)'}</span>
                  </button>

                  <button
                    type="button"
                    className="fwd-doc-btn-gallery"
                    onClick={() => handleTriggerGallery(docDef.key, docDef.label)}
                    disabled={uploadingDoc || isCancelled}
                  >
                    <span>📂</span>
                    <span>Dosya / Galeri</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 5. GÖRSEL KIRPMA VE ÇOKLU SAYFA MODALI */}
      {croppingImage && pendingDocKey && (
        <DocumentCropperModal
          image={croppingImage}
          documentLabel={pendingDocLabel}
          pageIndex={accumulatedFiles.length + 1}
          onTriggerNextUpload={() => {
            // iOS Safari Fix: Sonraki fotoğraf çekimi için kamera input'u senkron tetiklenir
            if (cameraInputRef.current) {
              cameraInputRef.current.value = '';
              cameraInputRef.current.click();
            }
          }}
          onCropComplete={handleCropComplete}
          onCancel={() => {
            setCroppingImage(null);
            setPendingDocKey(null);
            setAccumulatedFiles([]);
          }}
        />
      )}

      {/* 6. Ay Ziyareti & Not Modalı */}
      {selectedMonth && (
        <div className="fwd-modal-overlay" onClick={() => setSelectedMonth(null)}>
          <div className="fwd-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="fwd-modal-header">
              <h3 className="fwd-modal-title">
                {selectedMonth.monthName} {currentYear} Ziyareti
              </h3>
              <button
                type="button"
                className="fwd-modal-close"
                onClick={() => setSelectedMonth(null)}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSaveMonthVisit} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div className="fwd-form-group">
                <label className="fwd-form-label">Ziyaret Tarihi *</label>
                <input
                  type="date"
                  className="fwd-form-input"
                  value={monthVisitDate}
                  max={todayStr}
                  onChange={(e) => setMonthVisitDate(e.target.value)}
                  required
                />
                <span style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                  ℹ️ Ziyaret günü ve saati gelmeden bir ziyaret "YAPILDI" olarak kaydedilemez.
                </span>
              </div>

              <div className="fwd-form-group">
                <label className="fwd-form-label">Ziyaret Notu (İsteğe Bağlı)</label>
                <textarea
                  className="fwd-form-textarea"
                  value={monthNote}
                  onChange={(e) => setMonthNote(e.target.value)}
                  placeholder="Bu ziyaretle ilgili önemli gözlem veya bulgular..."
                />
              </div>

              <div className="fwd-modal-footer">
                {selectedMonth.visited && (
                  <button
                    type="button"
                    className="fwd-btn-danger"
                    onClick={handleDeleteMonthVisit}
                    disabled={savingVisit}
                  >
                    Ziyareti İptal Et
                  </button>
                )}

                <button
                  type="submit"
                  className="fwd-btn-primary"
                  disabled={savingVisit}
                  style={{ flex: 1 }}
                >
                  {savingVisit ? 'Kaydediliyor...' : '✓ Ziyareti Onayla'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 7. BELGE GÖRÜNTÜLEME MODALI (1, 2, 3.. SAYFALARI ARASINDA GEÇİŞLİ & MOBİL UYUMLU) */}
      {viewerDocList && (
        <div className="fwd-viewer-modal-overlay" onClick={closeDocViewer}>
          <div className="fwd-viewer-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="fwd-viewer-modal-header">
              <div className="fwd-viewer-header-info">
                <h3 className="fwd-viewer-modal-title">
                  👁️ {viewerTitle}
                </h3>
                <span className="fwd-viewer-modal-subtitle">
                  Sayfa {activeViewerIdx + 1} / {viewerDocList.length}
                </span>
              </div>
              <button
                type="button"
                className="fwd-viewer-modal-close"
                onClick={closeDocViewer}
              >
                ✕
              </button>
            </div>

            {/* Sayfalar Arası Hızlı Geçiş Butonları (1, 2, 3, 4, 5...) */}
            {viewerDocList.length > 1 && (
              <div className="fwd-viewer-page-switcher">
                {viewerDocList.map((doc, idx) => (
                  <button
                    key={doc.id}
                    type="button"
                    className={`fwd-viewer-page-btn ${idx === activeViewerIdx ? 'active' : ''}`}
                    onClick={() => handleViewerPageSelect(idx)}
                  >
                    Sayfa {idx + 1}
                  </button>
                ))}
              </div>
            )}

            {/* Önizleme Alanı */}
            <div className="fwd-viewer-body">
              {loadingViewer ? (
                <div className="fwd-viewer-loading">
                  <div className="field-spinner-large" style={{ borderTopColor: '#2563eb' }}></div>
                  <p>Sayfa Yükleniyor...</p>
                </div>
              ) : viewerBlobUrl ? (
                (() => {
                  const currentDoc = viewerDocList[activeViewerIdx];
                  const origName = (currentDoc?.originalFileName || currentDoc?.fileName || '').toLowerCase();
                  const isPdf = origName.endsWith('.pdf');
                  const isImg = /\.(jpg|jpeg|png|webp|gif|bmp|tiff|heic|heif)$/i.test(origName);

                  if (isPdf) {
                    return (
                      <iframe
                        src={viewerBlobUrl}
                        className="fwd-viewer-frame"
                        title={viewerTitle}
                      />
                    );
                  }

                  if (isImg) {
                    return (
                      <div className="fwd-viewer-image-wrapper">
                        <img
                          src={viewerBlobUrl}
                          alt={`${viewerTitle} - Sayfa ${activeViewerIdx + 1}`}
                          className="fwd-viewer-image"
                        />
                      </div>
                    );
                  }

                  return (
                    <div className="fwd-viewer-empty" style={{ padding: '20px' }}>
                      <span style={{ fontSize: '40px' }}>📄</span>
                      <strong style={{ color: '#ffffff', fontSize: '14px', textAlign: 'center' }}>{currentDoc?.originalFileName || currentDoc?.fileName}</strong>
                      <p style={{ color: '#cbd5e1', fontSize: '12px', textAlign: 'center', margin: '4px 0 12px 0' }}>
                        Word ve Excel belgeleri güvenlik ve format uyumluluğu nedeniyle cihazınızda indirilerek açılır.
                      </p>
                      <button
                        type="button"
                        onClick={() => archiveApi.downloadDocument(currentDoc.id, currentDoc.originalFileName || currentDoc.fileName)}
                        style={{
                          background: '#2563eb',
                          color: '#ffffff',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: '8px',
                          fontWeight: 700,
                          fontSize: '13px',
                          cursor: 'pointer'
                        }}
                      >
                        📥 Belgeyi İndir ve Aç
                      </button>
                    </div>
                  );
                })()
              ) : (
                <div className="fwd-viewer-empty">
                  <p>Belge önizlemesi yüklenemedi.</p>
                </div>
              )}
            </div>

            {/* Alt İşlem Butonları */}
            <div className="fwd-viewer-footer">
              {activeViewerIdx > 0 && (
                <button
                  type="button"
                  className="fwd-btn-page-nav"
                  onClick={() => handleViewerPageSelect(activeViewerIdx - 1)}
                >
                  ◀ Önceki Sayfa
                </button>
              )}

              {activeViewerIdx < viewerDocList.length - 1 && (
                <button
                  type="button"
                  className="fwd-btn-page-nav next"
                  onClick={() => handleViewerPageSelect(activeViewerIdx + 1)}
                >
                  Sonraki Sayfa ▶
                </button>
              )}

              <button
                type="button"
                className="fwd-btn-viewer-close"
                onClick={closeDocViewer}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldWorkplaceDetail;
